import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RefreshCw } from 'lucide-react';

interface SaveConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isSaving: boolean;
  changes: {
    trendsDeleted: number;
    trendsEdited: number;
    examplesDeleted: number;
  };
}

export function SaveConfirmationDialog({
  open,
  onClose,
  onConfirm,
  isSaving,
  changes
}: SaveConfirmationDialogProps) {
  const hasChanges = changes.trendsDeleted > 0 || changes.trendsEdited > 0 || changes.examplesDeleted > 0;

  return (
    <AlertDialog open={open} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Save Changes?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            {hasChanges ? (
              <div className="space-y-3">
                <p>You are about to save the following changes:</p>
                <ul className="list-disc pl-5 space-y-1 text-sm">
                  {changes.trendsDeleted > 0 && (
                    <li>
                      <strong>{changes.trendsDeleted}</strong> trend{changes.trendsDeleted === 1 ? '' : 's'} deleted
                    </li>
                  )}
                  {changes.trendsEdited > 0 && (
                    <li>
                      <strong>{changes.trendsEdited}</strong> trend{changes.trendsEdited === 1 ? '' : 's'} edited
                    </li>
                  )}
                  {changes.examplesDeleted > 0 && (
                    <li>
                      <strong>{changes.examplesDeleted}</strong> example{changes.examplesDeleted === 1 ? '' : 's'} removed
                    </li>
                  )}
                </ul>
                <p className="text-sm mt-3">
                  This will create a new draft version of the report. The changes will not affect any published version.
                </p>
              </div>
            ) : (
              <div>No changes detected to save.</div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSaving}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
            disabled={isSaving || !hasChanges}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isSaving ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
